import React, { useState, useEffect } from 'react';
import { userService } from 'src/services/user.service';
import { lookupService, Local as LookupLocal, Province } from 'src/services/lookups.service';
import { toast } from 'react-toastify';
import { mapUserFormDataToUserCreateDTO } from 'src/mappers/user.mapper';
import { RoleFormData, UserFormData } from 'src/pages/user-form-types';
import { TableUser, FormErrors, SortConfig, TableColumn } from 'src/components/user-management/types';
import Header from 'src/components/user-management/Header';
import UserTable from 'src/components/user-management/UserTable';
import UserFormDialog from 'src/components/user-management/UserFormDialog';
import ConfirmationDialog from 'src/components/user-management/ConfirmationDialog';
import UserDetailsOverlay from 'src/components/UserDetailsOverlay';
import { User } from 'src/services/user.service.types';
import { validateForm, formatPhoneNumber, getDefaultFormData, hasFormData, areLocalsRequired, hasValidLocals } from 'src/components/user-management/utils';
interface UserCreateResponse {
    message: string;
    user: User;
    emailError?: string;
}

const UserManagement: React.FC = () => {
    const [showBlockDialog, setShowBlockDialog] = useState(false);
    const [showAddUserDialog, setShowAddUserDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [showSaveConfirmDialog, setShowSaveConfirmDialog] = useState(false);
    const [showCancelConfirmDialog, setShowCancelConfirmDialog] = useState(false);
    const [formData, setFormData] = useState<UserFormData>(getDefaultFormData());
    const [showUserDetails, setShowUserDetails] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editFormData, setEditFormData] = useState<any>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState<TableUser[]>([]);
    const [errors, setErrors] = useState<FormErrors>({
        name: '',
        email: '',
        mobileNumber: '',
        role: '',
        region: '',
        local: ''
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const pageSize = 10;
    const [roles, setRoles] = useState<RoleFormData[]>([]);
    const [locals, setLocals] = useState<LookupLocal[]>([]);
    const [provinces, setProvinces] = useState<Province[]>([]);
    const [showRegionLocal, setShowRegionLocal] = useState(false);
    const [filteredLocals, setFilteredLocals] = useState<LookupLocal[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState<SortConfig>({
        key: 'name',
        direction: null
    });

    // Define table columns
    const columns: TableColumn[] = [
        { label: 'User', accessor: 'name', sortable: true },
        { label: 'Role', accessor: 'role', sortable: false },
        { label: 'Last Login', accessor: 'lastLogin', sortable: false },
        { label: 'Status', accessor: 'status', sortable: false },
    ];

    // Handle search
    const handleSearch = (searchTerm: string) => {
        setSearchTerm(searchTerm);
        // Reset to first page when searching
        setCurrentPage(1);
        getUsers(1, searchTerm);
    };

    // Handle row click
    const handleRowClick = async (item: any) => {
        try {
            // Fetch detailed user data by ID
            const userDetails = await userService.getUserById(item.id);
            // Transform the API response to match your UI needs
            const formattedUserDetails = {
                id: userDetails.id,
                name: userDetails.name,
                email: userDetails.email,
                role: userDetails.role,
                region: userDetails.region,
                mobileNumber: userDetails.mobileNumber,
                status: userDetails.status.charAt(0).toUpperCase() + userDetails.status.slice(1),
                lastLogin: userDetails.lastLogin
                    ? new Date(userDetails.lastLogin).toLocaleDateString('en-US', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                    })
                    : '-',
                updated_at: new Date(userDetails.updatedAt).toLocaleDateString('en-US', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                }),
                created_at: new Date(userDetails.createdAt).toLocaleDateString('en-US', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                }),
                local: userDetails.local ? userDetails.local.map(local => ({
                    id: local.id,
                    code: local.code,
                    unionNumber: local.unionNumber
                })) : []
            };

            setSelectedUser(formattedUserDetails);
            setShowUserDetails(true);
        } catch (error) {
            console.error('Failed to fetch user details:', error);
            toast.error('Failed to load user details');
        }
    };

    // Handle block user
    const confirmBlock = async () => {
        try {
            if (!selectedUser) return;
            if (isEditMode) {
                setEditFormData({
                    ...editFormData,
                    status: 'Inactive'
                });
                setSelectedUser({
                    ...selectedUser,
                    status: 'Inactive'
                });
            }
            setShowBlockDialog(false);
        } catch (error) {
            console.error('Failed to block user:', error);
            toast.error('Failed to block user. Please try again.');
        }
    };

    // Handle add user
    const handleAddUser = () => {
        setShowAddUserDialog(true);
    };

    // Validate form
    const validateUserForm = () => {
        const { isValid, errors: newErrors } = validateForm(formData, roles, filteredLocals);
        setErrors(newErrors);
        return isValid;
    };

    // Handle form submission
    const handleSubmit = () => {
        if (validateUserForm()) {
            setShowAddUserDialog(false);
            setShowSaveConfirmDialog(true);
        }
    };

    // Handle cancel save
    const handleCancelSave = () => {
        setShowSaveConfirmDialog(false);
        setShowAddUserDialog(true);
    };

    // Confirm save
    const confirmSave = async () => {
        setIsSubmitting(true);
        try {
            // Convert email to lowercase only when sending to the server
            const apiData = {
                ...mapUserFormDataToUserCreateDTO(formData, roles),
                email: formData.email.toLowerCase()
            };
            const response: UserCreateResponse = await userService.createUser(apiData);
            // Handle email error
            if (response.emailError) {
                toast.error("Failed to send welcome email: " + response.emailError);
            }
            setShowSaveConfirmDialog(false);
            // Reset form
            setEmptyFormData();
            // Reset errors
            setErrors({
                name: '',
                email: '',
                mobileNumber: '',
                role: '',
                region: '',
                local: ''
            });
            // Add success toast for user creation - important user action
            toast.success('User created successfully');

            await getUsers(currentPage);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            toast.error(errorMessage);
            setShowSaveConfirmDialog(false);
            setShowAddUserDialog(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        // For mobile number, format the input as user types
        if (name === 'mobileNumber') {
            const formatted = formatPhoneNumber(value);
            setFormData(prev => ({ ...prev, [name]: formatted }));

            // Clear error when input becomes valid
            const phoneRegex = /^(\+?1)?[-. ]?\(?(\d{3})\)?[-. ]?(\d{3})[-. ]?(\d{4})$/;
            if (phoneRegex.test(formatted)) {
                setErrors(prev => ({ ...prev, mobileNumber: '' }));
            }
        } else if (name === 'name') {
            setFormData(prev => ({ ...prev, [name]: value }));
            // Name validation with character limit
            if (!value.trim()) {
                setErrors(prev => ({ ...prev, name: 'Name is required' }));
            } else if (value.trim().length > 50) {
                setErrors(prev => ({ ...prev, name: 'Name must not exceed 50 characters' }));
            } else if (value.trim().length < 2) {
                setErrors(prev => ({ ...prev, name: 'Name must be at least 2 characters' }));
            } else if (!/^[a-zA-Z\s'-]*$/.test(value.trim())) {
                setErrors(prev => ({ ...prev, name: 'Name should only contain letters, spaces, apostrophes, and hyphens' }));
            } else {
                setErrors(prev => ({ ...prev, name: '' }));
            }
        } else if (name === 'email') {
            // Preserve original casing during input
            setFormData(prev => ({ ...prev, [name]: value }));

            // Clear email error when input becomes valid
            const emailRegexStrict = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+\.(com|org|co|io|ca)$/i;
            if (emailRegexStrict.test(value)) {
                setErrors(prev => ({ ...prev, email: '' }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    // Handle edit click
    const handleEditClick = () => {
        // Format the region data to match the expected structure
        const formattedUser = {
            ...selectedUser,
            region: selectedUser?.region ? 
                (Array.isArray(selectedUser.region) 
                    ? selectedUser.region.map((r: any) => r.id)
                    : [selectedUser.region.id]
                ) : []
        };
        setEditFormData(formattedUser);
        setIsEditMode(true);
    };

    // Handle edit input change
    const handleEditInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> |
        { target: { name: string; value: any } }
    ) => {
        setEditFormData((prev: any) => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    // Handle save edit
    const handleSaveEdit = async () => {
        if (!editFormData) return;

        try {
            const updateData = {
                id: editFormData.id,
                name: editFormData.name,
                email: editFormData.email.toLowerCase(), // Convert to lowercase when sending to server
                mobileNumber: editFormData.mobileNumber,
                status: editFormData.status.toLowerCase(),
                role: editFormData.role.id,
                region: Array.isArray(editFormData.region) ? editFormData.region : [],
                local: Array.isArray(editFormData.local)
                    ? editFormData.local
                    : []
            };

            await userService.updateUser(updateData);
            setSelectedUser(editFormData);
            setIsEditMode(false);
            setShowUserDetails(false);

            toast.success('User updated successfully');

            await getUsers(currentPage);
        } catch (error: any) {
            console.error('Failed to update user:', error);
            toast.error(error.message);
        }
    };

    // Get users
    const getUsers = async (page: number = 1, search: string = searchTerm) => {
        try {
            const response = await userService.getUsersPaginated({
                page,
                limit: pageSize,
                search: search,
                sortBy: sortConfig.direction ? 'name' : '',
                sortOrder: sortConfig.direction || ''
            });

            // Transform all users data without filtering
            const formattedUsers: TableUser[] = response.data.map((user) => ({
                id: user.id,
                name: user.name,
                email: user.email,
                mobileNumber: user.mobileNumber,
                role: user.role.roleName === 'interRepre' ? 'International Representative' :
                    user.role.roleName === 'organizer' ? 'Organizer' :
                        user.role.roleName === 'admin' ? 'Admin' :
                            user.role.roleName,
                region: user.region ? {
                    id: user.region.id,
                    name: user.region.name
                } : null,
                local: user.local || [],
                lastLogin: user.lastLogin
                    ? new Date(user.lastLogin).toLocaleDateString('en-US', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                    })
                    : '-',
                status: user.status.charAt(0).toUpperCase() + user.status.slice(1),
                createdAt: user.createdAt,
                updatedAt: user.updatedAt
            }));

            setUsers(formattedUsers);
            setTotalItems(response.totalCount);
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch users:', error);
            setLoading(false);
            toast.error('Failed to fetch users');
        }
    };

    // Handle sort
    const handleSort = (key: string, direction: 'asc' | 'desc' | null) => {
        setSortConfig({ key, direction });
        // Reset to first page when sorting changes
        setCurrentPage(1);
    };

    // Get roles
    const getRoles = async () => {
        try {
            const rolesData = await userService.getRoles();
            // Ensure rolesData is of the correct type
            const formattedRoles = rolesData.map(role => ({
                id: role.id, // Assuming 'id' exists in the role object
                name: role.roleName // Ensure 'name' is included
            }));
            setRoles(formattedRoles);
        } catch (error) {
            console.error('Failed to fetch roles:', error);
            toast.error('Failed to load roles');
        }
    };

    // Handle page change
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // Handle status change
    const handleStatusChange = async (userId: string, newStatus: string) => {
        try {
            await userService.updateUserStatus(userId, newStatus);
            toast.success(`User status updated successfully`);
            await getUsers(currentPage);
        } catch (error) {
            console.error('Failed to update user status:', error);
            toast.error('Failed to update user status');
            // Refresh to ensure UI shows correct state
            await getUsers(currentPage);
        }
    };

    // Fetch locals
    const fetchLocals = async () => {
        try {
            const localsData = await lookupService.getLocals();
            setLocals(localsData as LookupLocal[]);
        } catch (error) {
            console.error('Error fetching locals:', error);
        }
    };

    // Fetch provinces
    const fetchProvinces = async () => {
        try {
            const provincesData = await lookupService.getProvinces();
            setProvinces(provincesData);
        } catch (error) {
            console.error('Error fetching provinces:', error);
        }
    };

    // Handle role select
    const handleRoleSelect = (value: string) => {
        setFormData(prev => ({ ...prev, role: value }));

        // Clear role error only if a valid role is selected
        if (value && value !== '4') { // Don't clear error for default role
            setErrors(prev => ({ ...prev, role: '' }));
        } else {
            setErrors(prev => ({ ...prev, role: 'Please select a role' }));
        }

        const selectedRole = roles.find(role => role.id === value);
        setShowRegionLocal(selectedRole?.name === 'organizer');

        if (selectedRole?.name !== 'organizer') {
            setFormData(prev => ({
                ...prev,
                region: [''],
                local: []
            }));
            setErrors(prev => ({
                ...prev,
                region: '',
                local: ''
            }));
        }
    };

    // Handle region select
    const handleRegionSelect = (selected: string[]) => {
    const value = selected[0] ?? ''; // grab first region if you need it for filtering

    setFormData(prev => ({
        ...prev,
        region: selected, // now this is correctly a string[]
        local: [] // reset local selection when region changes
    }));

    // Clear region error when a valid region is selected
    if (value) {
        setErrors(prev => ({ ...prev, region: '', local: '' }));
    }

    const regionLocals = locals.filter(local => local.province?.id === value);
    setFilteredLocals(regionLocals);

    // Auto-select local if there's only one valid option or for specific provinces
    const selectedProvince = provinces.find(province => province.id === value);
    const validLocals = regionLocals.filter(
        local => local.unionNumber && String(local.unionNumber).toUpperCase() !== 'N/A'
    );

    if (
        validLocals.length === 1 ||
        (selectedProvince && (selectedProvince.name === 'Nunavut' || selectedProvince.name === 'Yukon') && validLocals.length > 0)
    ) {
        const singleLocal = validLocals[0];
        if (singleLocal) {
            setFormData(prev => ({
                ...prev,
                local: [singleLocal.id]
            }));
        }
    }

    if (!hasValidLocals(regionLocals)) {
        setFormData(prev => ({
            ...prev,
            local: []
        }));
        setErrors(prev => ({
            ...prev,
            local: ''
        }));
    }
};

        // {
        //     const selectedProvince = provinces.find(province => province.id === value);
        //     const validLocals = regionLocals.filter(local => local.unionNumber && String(local.unionNumber).toUpperCase() !== 'N/A');
            
        //     // Auto-select if there's only one valid local or if it's Nunavut/Yukon
        //     if (validLocals.length === 1 || 
        //         (selectedProvince && (selectedProvince.name === 'Nunavut' || selectedProvince.name === 'Yukon') && validLocals.length > 0)) {
        //         const singleLocal = validLocals[0];
        //         if (singleLocal) {
        //             setFormData(prev => ({
        //                 ...prev,
        //                 region: [value],
        //                 local: [singleLocal.id]
        //             }));
        //         }
        //     }
            
        //     // If no valid locals, clear local selection and don't require it
        //     if (!hasValidLocals(regionLocals)) {
        //         setFormData(prev => ({
        //             ...prev,
        //             region: [value],
        //             local: []
        //         }));
        //         setErrors(prev => ({
        //             ...prev,
        //             local: '' // Clear local error if no valid locals available
        //         }));
        //     }
        // }
    // };

    // Handle local select
    const handleLocalSelect = (selected: string[]) => {
        setFormData(prev => ({
            ...prev,
            local: selected
        }));

        // Clear local error if at least one local is selected
        if (selected.length > 0) {
            setErrors(prev => ({ ...prev, local: '' }));
        }
    };

    // Set empty form data
    const setEmptyFormData = () => {
        setFormData(getDefaultFormData());
    };

    // Reset form state
    const resetFormState = () => {
        setEmptyFormData();
        setErrors({
            name: '',
            email: '',
            mobileNumber: '',
            role: '',
            region: '',
            local: ''
        });
        setShowRegionLocal(false);
        setFilteredLocals([]);
    };

    // Handle dialog close
    const handleDialogClose = () => {
        if (hasFormData(formData)) {
            setShowCancelConfirmDialog(true);
        } else {
            resetFormState();
            setShowAddUserDialog(false);
        }
    };

    // Confirm dialog close
    const confirmDialogClose = () => {
        setShowCancelConfirmDialog(false);
        resetFormState();
        setShowAddUserDialog(false);
    };

    // Cancel dialog close
    const cancelDialogClose = () => {
        setShowCancelConfirmDialog(false);
    };

    // Load initial data
    useEffect(() => {
        getRoles();
        getUsers(currentPage);
    }, [currentPage, pageSize, searchTerm, sortConfig.direction]);

    // Fetch locals
    useEffect(() => {
        fetchLocals();
    }, []);

    // Fetch provinces
    useEffect(() => {
        fetchProvinces();
    }, []);

    // Handle body scroll
    useEffect(() => {
        if (showAddUserDialog || showBlockDialog) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        // Cleanup function
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showAddUserDialog, showBlockDialog]);

    return (
        <div id="user-management-container" className="bg-gray-50 p-3 sm:p-4 md:p-6">
            <div className="">
                <Header 
                    searchTerm={searchTerm}
                    onSearch={handleSearch}
                    onAddUser={handleAddUser}
                />

                <div className="bg-white rounded-card shadow overflow-hidden p-3 sm:p-4">
                    <div className="relative min-h-[300px]">
                        <UserTable 
                            users={users}
                            columns={columns}
                            totalItems={totalItems}
                            currentPage={currentPage}
                            pageSize={pageSize}
                            loading={loading}
                            sortConfig={sortConfig}
                            onPageChange={handlePageChange}
                            onSearch={handleSearch}
                            onRowClick={handleRowClick}
                            onStatusChange={handleStatusChange}
                            onSort={handleSort}
                        />
                    </div>
                </div>
            </div>

            {/* Add User Dialog */}
            <UserFormDialog 
                isOpen={showAddUserDialog}
                title="Create User"
                formData={formData}
                errors={errors}
                roles={roles}
                provinces={provinces}
                filteredLocals={filteredLocals}
                showRegionLocal={showRegionLocal}
                isSubmitting={isSubmitting}
                onClose={handleDialogClose}
                onInputChange={handleInputChange}
                onRoleSelect={handleRoleSelect}
                onRegionSelect={handleRegionSelect}
                onLocalSelect={handleLocalSelect}
                onSubmit={handleSubmit}
            />

            {/* Block Confirmation Dialog */}
            <ConfirmationDialog 
                id="block-user"
                isOpen={showBlockDialog}
                title="Are you sure?"
                message="User will be Inactive"
                confirmText="Yes"
                cancelText="No"
                onConfirm={confirmBlock}
                onCancel={() => setShowBlockDialog(false)}
            />

            {/* User Details Overlay */}
            <UserDetailsOverlay
                isOpen={showUserDetails}
                onClose={() => {
                    setShowUserDetails(false);
                    setIsEditMode(false);
                }}
                user={selectedUser}
                isEditMode={isEditMode}
                onEditClick={handleEditClick}
                onSaveEdit={handleSaveEdit}
                editFormData={editFormData}
                onEditInputChange={handleEditInputChange}
                onShowBlockDialog={() => setShowBlockDialog(true)}
            />

            {/* Save Confirmation Dialog */}
            <ConfirmationDialog 
                id="save-user"
                isOpen={showSaveConfirmDialog}
                title="Confirmation"
                message={`Are you sure to add the new user "${formData?.name || ''}"?`}
                confirmText="Yes"
                cancelText="No"
                isSubmitting={isSubmitting}
                onConfirm={confirmSave}
                onCancel={handleCancelSave}
            />

            {/* Cancel Confirmation Dialog */}
            <ConfirmationDialog 
                id="cancel-user"
                isOpen={showCancelConfirmDialog}
                title="Discard Changes"
                message="Are you sure you want to discard your changes?"
                confirmText="Yes"
                cancelText="No"
                onConfirm={confirmDialogClose}
                onCancel={cancelDialogClose}
            />
        </div>
    );
};

export default UserManagement;
